import type { TransferState } from '@angular/core';
import { makeStateKey } from '@angular/core';
import { ReplaySubject } from 'rxjs';

import type { IEnvironmentService } from './environment.interface';

export interface Environment {
	/**
	 * The API URL
	 */
	api: string;
	/**
	 * The accessoPay URL
	 */
	apay: string;
	/**
	 * The assets URL
	 */
	assets: string;
	/**
	 * The environment name we're in
	 */
	env: string;
	/**
	 * If we're in a production environment
	 */
	isProduction: boolean;
}

export const APP_ENVIRONMENT_KEY = makeStateKey<Environment>('app-environment-data');

/**
 * The base environment service that has most of the implementation for both SSR and browser
 */
export abstract class AEnvironmentService implements IEnvironmentService {
	/**
	 * Our internal loaded state
	 */
	protected readonly loaded = new ReplaySubject<true>(1);
	/**
	 * Holds the enviroment information
	 */
	protected _environment: Environment | undefined;
	/**
	 * Holds the store unique third-level domain
	 */
	protected _store: string | undefined;
	/**
	 * A readonly observable of the loaded state
	 */
	readonly loaded$ = this.loaded.asObservable();
	/**
	 * Our constructor
	 * @constructor
	 * @param transfer The transfer state service
	 */
	constructor(protected readonly transfer: TransferState) {}
	/**
	 * Set the store subdomain
	 */
	set store(value: string) {
		this._store = value;
	}
	/**
	 * Get the store subdomain
	 */
	get store(): string {
		return this._store ?? '';
	}
	/**
	 * Returns the api url
	 */
	get api(): string {
		return this._environment?.api ?? '';
	}
	/**
	 * Returns the environment's apay url
	 */
	get apay(): string {
		return this._environment?.apay ?? '';
	}
	/**
	 * Returns the assets url
	 */
	get assets(): string {
		return this._environment?.assets ?? '';
	}
	/**
	 * Returns the environment name
	 */
	get env(): string {
		return this._environment?.env ?? 'unknown';
	}
	/**
	 * Find out if we're in production
	 */
	get isProduction(): boolean {
		return this._environment?.isProduction ?? true;
	}
}
